/**
 * Centralized date / time / duration formatting.
 *
 * All pages render dates through this module. If you find yourself
 * reaching for `new Date(...).toLocaleString()` or string `.slice(0, 10)`
 * on a timestamp, add the missing helper here instead.
 *
 * Locale-aware as of the i18n scaffold commit. dayjs's locale is kept
 * in sync with i18next's current language so `formatRelative()`
 * ("2 hours ago") translates when we add fr/es. To wire a new locale,
 * drop in the matching `dayjs/locale/{xx}` import here.
 *
 * The relativeTime plugin is registered once at module load; subsequent
 * imports are no-ops.
 */

import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import i18n from '@/lib/i18n'

dayjs.extend(relativeTime)

// English-only at launch — dayjs ships with 'en' as default so we don't
// need to import any locale data file for that. When fr/es land:
//   import 'dayjs/locale/fr'
//   import 'dayjs/locale/es'
// (side-effect imports — they auto-register the locale data with dayjs.)

function syncDayjsLocale(lang: string) {
  // dayjs locale codes are typically the base language without region
  // ('en' rather than 'en-CA'). i18next reports `i18n.language` which
  // includes region, so we strip it for dayjs.
  const base = (lang || 'en').split('-')[0]
  dayjs.locale(base)
}

syncDayjsLocale(i18n.language ?? 'en')
i18n.on('languageChanged', syncDayjsLocale)

type DateInput = string | number | Date | null | undefined

const EM_DASH = '—'

function parse(d: DateInput): dayjs.Dayjs | null {
  if (d === null || d === undefined || d === '') return null
  const value = dayjs(d)
  return value.isValid() ? value : null
}

/**
 * Render a date as "May 8, 2026". Returns "—" for null / undefined /
 * unparseable input.
 *
 * Use for DATE columns from Postgres (`first_visit`, `service_date`,
 * `casl_consent_date`, etc.) — anything where the time-of-day is
 * irrelevant.
 */
export function formatDate(d: DateInput): string {
  const v = parse(d)
  return v ? v.format('MMM D, YYYY') : EM_DASH
}

/**
 * Render a timestamp as "May 8, 2026 · 3:42 PM". Returns "—" for null /
 * undefined / unparseable input.
 *
 * Use for TIMESTAMPTZ columns (`created_at`, `started_at`, etc.) — any
 * time the time-of-day matters.
 */
export function formatDateTime(d: DateInput): string {
  const v = parse(d)
  return v ? v.format('MMM D, YYYY · h:mm A') : EM_DASH
}

/**
 * Render a timestamp as "2 hours ago" / "in 5 minutes" / etc. via
 * dayjs's relativeTime plugin. Returns "—" for null / undefined /
 * unparseable input.
 *
 * Use for activity timelines, "last seen" fields, and anywhere a
 * relative-time read is more useful than an absolute one.
 */
export function formatRelative(d: DateInput): string {
  const v = parse(d)
  return v ? v.fromNow() : EM_DASH
}

/**
 * Short weekday + time ("Thu 2:00 PM") — receipt sublines (DESIGN.md
 * §4.1). Locale rides dayjs like every other helper here.
 */
export function formatShortWhen(d: DateInput): string {
  return dayjs(d).format('ddd h:mm A')
}

/**
 * Parse a `YYYY-MM-DD` day token into a Date at LOCAL midnight of that
 * day. Returns null for missing / malformed
 * input. The local-midnight construction matters: `new Date('2026-06-01')`
 * parses as UTC midnight and shifts a day backward in western timezones,
 * which would land the calendar on the wrong day.
 */
export function parseLocalDate(s: string | null | undefined): Date | null {
  if (!s) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return null
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  const dt = new Date(year, month - 1, day)
  return Number.isNaN(dt.getTime()) ? null : dt
}

/**
 * Render a duration (in seconds) as "4m 12s". Returns "—" for null /
 * undefined / non-finite input. Negative seconds are treated as their
 * absolute value.
 *
 * Helper kept here for future use — the calls table currently stores
 * pre-formatted strings (`duration`, `elapsed`) rather than seconds, so
 * no current page calls this. When we wire raw call durations from
 * Vapi (Phase 3), use this.
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) {
    return EM_DASH
  }
  const total = Math.max(0, Math.round(Math.abs(seconds)))
  const m = Math.floor(total / 60)
  const s = total % 60
  return m === 0 ? `${s}s` : `${m}m ${s}s`
}
